/**
 * Upgraded Application Configuration
 * Enhanced action types for centralized variation/version management
 */

// Import all existing configuration
import { config, allRoutes, allStates, docFilterOptions, itemsPerPageOptions, literaryGroupTypes, formixConfig_VoiceLab, characterTypeList, characterGenderList } from './config';

// Re-export all existing configuration
export { config, allRoutes, allStates, docFilterOptions, itemsPerPageOptions, literaryGroupTypes, formixConfig_VoiceLab, characterTypeList, characterGenderList };

// Enhanced action types for centralized variation/version management
export const ACTION_TYPES_UPGRADE = {
  // Load Actions (Form Population) - These trigger data fetching and form loading
  LOAD_VARIATION: 'load_variation',                    // From VariationList selection
  LOAD_VARIATION_VERSION: 'load_variation_version',    // From VersionsList row/edit/checkbox
  LOAD_DEFAULT_VERSION: 'load_default_version',        // New Variation button
  LOAD_CLONE: 'load_clone',                           // Use as Base button

  // Save Actions (Form Submission) - These handle form data persistence
  SAVE_UPDATE_VERSION: 'save_update_version',         // Update existing variation
  SAVE_NEW_VERSION: 'save_new_version',               // Save new variation
  SAVE_CLONE_VERSION: 'save_clone_version',           // Save cloned version (Use as Base)

  // Delete Actions - These handle deletion operations
  DELETE_VERSION: 'delete_version',                   // Delete version (modal confirmation)

  // UI State Actions - These handle UI-only state changes
  SET_ACTIVE_SAMPLE: 'set_active_sample',            // Update activeSampleUuid only
  CLEAR_FORM: 'clear_form',                          // Reset form to empty state
  SET_LOADING: 'set_loading',                        // Set loading state
  SET_ERROR: 'set_error',                            // Set error state
  CLEAR_ERROR: 'clear_error',                        // Clear error state
  SHOW_ERROR_HINT: 'show_error_hint',                // Show 3-second error hint

  // Field Change Actions - These handle individual field updates
  FIELD_CHANGE: 'field_change',                      // Individual field changes
  STATUS_CHANGE: 'status_change',                    // Status-related field changes

  // Legacy Actions (for backward compatibility during transition)
  SAMPLE_PREVIEW: 'sample_preview',                  // Legacy preview action
  SAMPLE_EDIT_SELECT: 'SAMPLE_EDIT_SELECT',                    // Legacy select action
  CANCEL_CHANGES: 'cancel_changes'                   // Legacy cancel action
};

// Version types for data loading strategies
export const VERSION_TYPES_UPGRADE = {
  ACTIVE_SAMPLE: 'active_sample',                    // Load the active sample for a variation
  SPECIFIC_SAMPLE: 'specific_sample',                // Load a specific sample by ID
  BASE_VERSION: 'base_version',                      // Load base variation data only
  VARIATION_ONLY: 'variation_only',                  // Load variation metadata only
  DEFAULT_VERSION: 'default_version',                // Load empty default structure
  CLONE_VERSION: 'clone_version'                     // Load data for cloning operation
};

// Edit modes for form behavior
export const EDIT_MODES = {
  NEW: 'new',                                        // Creating new variation
  EDIT: 'edit',                                      // Editing existing variation
  CLONE: 'clone',                                    // Cloning from existing version
  VIEW: 'view'                                       // View-only mode
};

// Loading states for UI feedback
export const LOADING_STATES = {
  IDLE: 'idle',                                      // No operation in progress
  LOADING: 'loading',                                // Data loading in progress
  SAVING: 'saving',                                  // Save operation in progress
  DELETING: 'deleting',                              // Delete operation in progress
  ERROR: 'error'                                     // Error state
};

// Error types for error handling
export const ERROR_TYPES = {
  LOAD_FAILED: 'load_failed',                        // Failed to load data
  SAVE_FAILED: 'save_failed',                        // Failed to save data
  DELETE_FAILED: 'delete_failed',                    // Failed to delete data
  VALIDATION_FAILED: 'validation_failed',            // Form validation failed
  NETWORK_ERROR: 'network_error',                    // Network connectivity issue
  PERMISSION_DENIED: 'permission_denied'             // Insufficient permissions
};

// Default values for new variations
export const DEFAULT_VARIATION = {
  uuid: null,
  name: '',
  content: '',
  priority: 'medium',
  status: 'not-started',
  version: 1,
  s3Url: '',
  fileDuration: 0,
  activeSampleUuid: null,
  deadline: null,
  characterId: null,
  createdAt: null,
  updatedAt: null
};

// Default values for new versions/samples
export const DEFAULT_VERSION = {
  uuid: null,
  textSnippet: '',
  s3Url: '',
  version: 1,
  fileDuration: 0,
  isActive: false,
  createdAt: null,
  updatedAt: null
};

// Action configuration for useEditVersionManager
export const ACTION_CONFIGS = {
  [ACTION_TYPES_UPGRADE.LOAD_VARIATION]: {
    versionType: VERSION_TYPES_UPGRADE.ACTIVE_SAMPLE,
    editMode: EDIT_MODES.EDIT,
    clearForm: false,
    clearErrors: true,
    showLoading: true
  },
  [ACTION_TYPES_UPGRADE.LOAD_VARIATION_VERSION]: {
    versionType: VERSION_TYPES_UPGRADE.SPECIFIC_SAMPLE,
    editMode: EDIT_MODES.EDIT,
    clearForm: false,
    clearErrors: true,
    showLoading: true
  },
  [ACTION_TYPES_UPGRADE.LOAD_DEFAULT_VERSION]: {
    versionType: VERSION_TYPES_UPGRADE.DEFAULT_VERSION,
    editMode: EDIT_MODES.NEW,
    clearForm: true,
    clearErrors: true,
    showLoading: false
  },
  [ACTION_TYPES_UPGRADE.LOAD_CLONE]: {
    versionType: VERSION_TYPES_UPGRADE.CLONE_VERSION,
    editMode: EDIT_MODES.CLONE,
    clearForm: false,
    clearErrors: true,
    showLoading: true
  },
  [ACTION_TYPES_UPGRADE.FIELD_CHANGE]: {
    versionType: null,
    editMode: null,
    clearForm: false,
    clearErrors: false,
    showLoading: false
  },
  [ACTION_TYPES_UPGRADE.CLEAR_FORM]: {
    versionType: VERSION_TYPES_UPGRADE.DEFAULT_VERSION,
    editMode: EDIT_MODES.NEW,
    clearForm: true,
    clearErrors: true,
    showLoading: false
  }
};

// API endpoints specific to the upgraded system
export const API_ENDPOINTS_UPGRADE = {
  // Use existing endpoint for fetching variation/version data
  GET_CHAR_VAR_SAMPLES: config.GET_CHAR_VAR_SAMPLES,

  // Direct API endpoints for CRUD operations
  GET_VARIATION: config.GET_API_DIRECT_VARIATION,
  POST_VARIATION: config.POST_API_DIRECT_VARIATION,
  PUT_VARIATION: config.PUT_API_DIRECT_VARIATION,
  DELETE_VARIATION: config.DELETE_API_DIRECT_VARIATION,

  // Character speech sample endpoints
  ADD_CHARACTER_SPEECH_SAMPLE: config.ADD_CHARACTER_SPEECH_SAMPLE_V2,
  DELETE_SAMPLE: config.DELETE_API_SAMPLE,

  // File upload endpoints
  UPLOAD_VARIATION_AUDIO: config.UPLOAD_BOOK_VARIATION_AUDIO
};

// Export default as the original config for backward compatibility
export default config;
